import {
  AppInsightsErrorBoundary,
  withAITracking,
} from "@microsoft/applicationinsights-react-js";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
  useIsAuthenticated,
} from "@azure/msal-react";
import { BrowserRouter } from "react-router-dom";
import { InteractionStatus } from "@azure/msal-browser";
import React, { createContext, useEffect } from "react";
import { isNull, isUndefined } from "lodash";

import { AppConfigs } from "../../models/AppConfigs";
import { getLocalStorageData, getValue, setLocalStorageData } from "../../utils";
import { loginRequest } from "../..";
import { reactPlugin } from "../../utils/logHandlers/appInsight";
import CentralLoader from "../../components/custom/Loading/components/CentralLoader";
import ErrorPage from "../../components/errorPages/404"; //TBD
import Header from "../containers/Header";
import RoutesList from "../../routes";
import { UserModuleResponse } from "../../models/Api/Modules";
import UnAuthorizedPage from "../../components/errorPages/401";
import { RouteUrlInfo, UserPermission } from "../../models/User";
import { PriorAuthMasterApiKey } from "../../constants/Constants";
import { masterSyncPayload } from "../../constants/MasterCache";
import { updateConfigs } from "constants/Config";

export interface PropsFromState {
  config: AppConfigs;
  moduleData: UserModuleResponse;
  userPermission: UserPermission[];
  routeUrlInfo: RouteUrlInfo;
  permissionState: string[];
}

export interface PropsFromDispatch {
  getConfig: (configFileName: string) => void;
  saveRouteUrlInfo: (routeUrlInfo: RouteUrlInfo) => void;
  getAccessControlId: (controlIds: string[]) => void;
  getConfigVariables: () => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

export const PermissionContext = createContext<string[]>([]);

const App: React.FC<AllProps> = ({
  config,
  moduleData,
  userPermission,
  saveRouteUrlInfo,
  routeUrlInfo,
  getAccessControlId,
  permissionState,
  getConfigVariables
}) => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  useEffect(() => {
    if (inProgress === InteractionStatus.None && !isAuthenticated) {
      instance.loginRedirect(loginRequest);
    }
    if (isAuthenticated) { 
      getConfigVariables();
    }
  }, [isAuthenticated, inProgress, instance]);

  const [isConfigLoaded, setIsConfigLoaded] = React.useState(false);
  useEffect(() => { 
    if (config && config.REACT_APP_TEMPO_APPINSIGHTS_CONNECTION_STRING.length > 0) {
      updateConfigs(config);
      setTimeout(() => { 
        setIsConfigLoaded(true);
      }, 500);
    }
  }, [config]);

  useEffect(() => {

    if(isNull(getLocalStorageData(PriorAuthMasterApiKey))){
      setLocalStorageData(
        PriorAuthMasterApiKey,
        masterSyncPayload
      );
    }
  }, []);

  useEffect(() => {
    if (getValue(routeUrlInfo, "moduleId") >= 1) {
      const moduleId = routeUrlInfo.moduleId;
      const subModuleId = isUndefined(routeUrlInfo.subModuleId)
        ? null
        : routeUrlInfo.subModuleId;
      const userAccess = userPermission.filter(
        (permission: UserPermission) =>
          permission.moduleId == moduleId &&
          permission.subModuleId == subModuleId
      );
      const accessControlId = userAccess.map(
        (permission: UserPermission) => permission.htmlControlId || ""
      );
      getAccessControlId(accessControlId);
    }
  }, [routeUrlInfo, userPermission]);


  return (
    <div>
      <AuthenticatedTemplate>
        {isConfigLoaded ? (
          <React.Fragment>
            <AppInsightsErrorBoundary
              onError={() => ErrorPage()}
              appInsights={reactPlugin}
            >
              <BrowserRouter>
                <PermissionContext.Provider value={permissionState}>
                  <Header />
                  {moduleData.response !== null ? (
                    <RoutesList
                      moduleData={moduleData}
                      saveRouteUrlInfo={saveRouteUrlInfo}
                    />
                  ) : (
                    <UnAuthorizedPage></UnAuthorizedPage>
                  )}
                  <CentralLoader />
                </PermissionContext.Provider>
              </BrowserRouter>
            </AppInsightsErrorBoundary>
          </React.Fragment>
        ) : (
          <React.Fragment></React.Fragment>
        )}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <div>Signing in...</div>
      </UnauthenticatedTemplate>
    </div>
  );
};

export default withAITracking(reactPlugin, App);
